import React from 'react';
import { motion } from 'framer-motion';
import WhatsAppButton from '../components/WhatsAppButton';

const ProductsServices = () => {
  const products = [
    {
      name: 'Crocin',
      description: 'Effective pain relief and fever reducer',
      image: 'https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?crop=entropy&cs=srgb&fm=jpg&q=85'
    },
    {
      name: 'Rantac',
      description: 'Trusted relief from acidity and heartburn',
      image: 'https://images.unsplash.com/photo-1631980839224-2554f0cb39f1?crop=entropy&cs=srgb&fm=jpg&q=85'
    },
    {
      name: 'Volini',
      description: 'Quick relief for muscle and joint pain',
      image: 'https://images.unsplash.com/photo-1655361166052-a65fa7633820?crop=entropy&cs=srgb&fm=jpg&q=85'
    },
    {
      name: 'Bandages',
      description: 'High-quality medical bandages and dressings',
      image: 'https://images.unsplash.com/photo-1603398938378-e54eab446dde?crop=entropy&cs=srgb&fm=jpg&q=85'
    }
  ];

  const services = [
    {
      title: 'Allopathy Medicines',
      description: 'Wide range of allopathic medicines from trusted brands',
      image: 'https://images.unsplash.com/photo-1631980839224-2554f0cb39f1?crop=entropy&cs=srgb&fm=jpg&q=85',
      icon: '💊'
    },
    {
      title: 'Homeopathy Medicines',
      description: 'Comprehensive collection of homeopathic remedies',
      image: 'https://images.unsplash.com/photo-1671493234884-b1611bcf3e69?crop=entropy&cs=srgb&fm=jpg&q=85',
      icon: '🌿'
    },
    {
      title: 'Doctor Appointments',
      description: 'Schedule consultations with qualified healthcare professionals',
      image: 'https://images.unsplash.com/photo-1615462696310-09736533dbb8?crop=entropy&cs=srgb&fm=jpg&q=85',
      icon: '👨‍⚕️'
    },
    {
      title: 'Blood Sugar Testing',
      description: 'Quick and accurate blood glucose level monitoring',
      image: 'https://images.unsplash.com/photo-1599814516324-66aa0bf16425?crop=entropy&cs=srgb&fm=jpg&q=85',
      icon: '🩺'
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 }
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="products-services-page">
      {/* Hero Section */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-medical-mint/30 to-white">
        <div className="max-w-7xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1
              data-testid="page-heading"
              className="text-5xl md:text-6xl font-playfair font-bold text-medical-green mb-6"
            >
              Products & Services
            </h1>
            <p className="text-xl md:text-2xl font-manrope text-gray-600 max-w-3xl mx-auto">
              Quality healthcare products and services for your wellbeing
            </p>
          </motion.div>
        </div>
      </section>

      {/* Products Section */}
      <section className="py-20 md:py-32 px-4 sm:px-6 lg:px-8 bg-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={containerVariants}
          className="max-w-7xl mx-auto"
        >
          <motion.div variants={itemVariants} className="mb-16">
            <h2
              data-testid="products-heading"
              className="text-4xl md:text-5xl font-playfair font-bold text-medical-green mb-4"
            >
              Our Products
            </h2>
            <p className="text-lg text-gray-600 font-manrope">
              Genuine medicines from trusted pharmaceutical companies
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-testid="products-grid">
            {products.map((product, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                data-testid={`product-card-${index}`}
                className="bg-white rounded-2xl p-6 border border-gray-200 hover:border-medical-green/30 transition-all duration-300 group cursor-pointer relative overflow-hidden shadow-sm hover:shadow-xl"
              >
                <div className="relative overflow-hidden rounded-xl mb-4" style={{ aspectRatio: '1/1' }}>
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <h3 className="text-2xl font-playfair font-bold text-medical-green mb-2 group-hover:text-medical-green-dark transition-colors duration-300">
                  {product.name}
                </h3>
                <p className="text-gray-600 font-manrope">{product.description}</p>
                
                {/* Hover Effect */}
                <div className="absolute inset-0 bg-medical-green/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Services Section */}
      <section className="py-20 md:py-32 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-white to-medical-mint/20">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={containerVariants}
          className="max-w-7xl mx-auto"
        >
          <motion.div variants={itemVariants} className="mb-16">
            <h2
              data-testid="services-heading"
              className="text-4xl md:text-5xl font-playfair font-bold text-medical-green mb-4"
            >
              Our Services
            </h2>
            <p className="text-lg text-gray-600 font-manrope">
              Comprehensive healthcare services for the community
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-testid="services-grid">
            {services.map((service, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                data-testid={`service-card-${index}`}
                className="bg-white rounded-2xl p-8 hover:shadow-2xl transition-all duration-300 border border-transparent hover:border-medical-green/10 group cursor-pointer"
              >
                <div className="flex items-start space-x-6">
                  <div className="flex-shrink-0">
                    <div className="w-20 h-20 bg-medical-mint rounded-2xl flex items-center justify-center group-hover:bg-medical-green group-hover:scale-110 transition-all duration-300">
                      <span className="text-4xl" role="img" aria-label={service.title}>
                        {service.icon}
                      </span>
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-playfair font-bold text-medical-green mb-3 group-hover:text-medical-green-dark transition-colors duration-300">
                      {service.title}
                    </h3>
                    <p className="text-gray-600 font-manrope text-lg leading-relaxed">
                      {service.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      <WhatsAppButton />
    </div>
  );
};

export default ProductsServices;
